import React, { Dispatch, createContext, useState, useRef } from 'react';
import { ActionBtns, Scrollable, Spinner, SimpleForm } from '@core/components';
import {
  SimpleFormItem,
  SimpleFormItemNoLabel,
  FieldValue
} from '@core/components/src/SimpleForm/SimpleForm';
import { useSendData } from '@core/hooks';
import {
  RowData,
  SendAddFn,
  SendUpdateFn
} from '@core/hooks/src/devExtreme/useSendData';
import { ValidationGroup } from 'devextreme-react';
import { XDevExtreme } from '@core/utils';

type WithPopupFormProps = {
  data: RowData;
  ready: boolean;
  setShowPopup: Dispatch<boolean>;
};

interface PopupFormContextValue {
  setFormItemValue: (name: string, value: FieldValue) => void;
  getFormItemValue: (fieldName: string) => FieldValue;
}

export const PopupFormContext = createContext<PopupFormContextValue>(
  {} as PopupFormContextValue
);

export const PopupFormItem = SimpleFormItem;
export const PopupFormItemNoLabel = SimpleFormItemNoLabel;

const getInitStateValue = (data: RowData) => {
  if (!data.mode) {
    const dataCopy = Object.entries(data).reduce<any>(
      (acc, [key, val]) => {
        if (key === 'mode') return acc;

        return { ...acc, [key]: val };
      },
      { ...data }
    );

    return dataCopy;
  }

  return {};
};

interface FormValue {
  [fieldName: string]: FieldValue;
}

interface WithPopupFormOptions<TData> {
  addFn?: SendAddFn<TData> | null | undefined;
  updateFn?: SendUpdateFn<TData> | null | undefined;
}

interface ValidationGroupRef {
  instance: () => { validate: () => { isValid: boolean } };
}

export type PopupFormComponentProps<
  TFormValue = FormValue
> = WithPopupFormProps & {
  formValue: TFormValue;
};

const withPopupForm = <TData,>(
  WrappedComponent: (props: PopupFormComponentProps<TData>) => JSX.Element,
  { addFn, updateFn }: WithPopupFormOptions<TData>
) => (props: WithPopupFormProps) => {
  const { data, setShowPopup, ready } = props;

  const validationGroupRef = useRef<ValidationGroupRef>(null);

  const [formValue, setFormValue] = useState<TData>(() =>
    getInitStateValue(data)
  );

  const [handleSave] = useSendData<TData>(
    data,
    formValue,
    setShowPopup,
    addFn,
    updateFn
  );

  const beforeSave = () => {
    if (!validationGroupRef.current) return;

    const { isValid } = validationGroupRef.current.instance().validate();

    if (!isValid) return;

    handleSave();
  };

  if (!ready) {
    return <Spinner />;
  }

  return (
    <ValidationGroup
      onInitialized={(e) => XDevExtreme.setInstanceToRef(validationGroupRef, e)}
      className="popup-content--with-btns"
    >
      <Scrollable>
        <SimpleForm<TData> formValue={formValue} setFormValue={setFormValue}>
          <WrappedComponent {...props} formValue={formValue} />
        </SimpleForm>
      </Scrollable>
      <ActionBtns
        handleSave={beforeSave}
        handleCancel={() => setShowPopup(false)}
      />
    </ValidationGroup>
  );
};

export default withPopupForm;
